/**
 * Panel "Información del Sistema" del Inicio (réplica del SSR).
 *
 * Presentacional: los datos llegan de `useAuth` (usuario, rol, sede) y de
 * `useDashboardStats` (resumen rápido y última actualización). Compone los
 * primitivos de `components/ui`. El sub-bloque "Resumen Rápido" solo se pinta
 * para admin (paridad estricta con `{% if user.role == 'admin' %}` del SSR); el
 * resto del panel lo ve cualquier usuario autenticado.
 *
 * Llamar a `useDashboardStats()` aquí no provoca un segundo fetch: comparte la
 * misma query key que la pantalla, así que el caché de TanStack Query lo deduplica.
 */
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import type { BadgeTone } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { formatUpdatedAt, roleLabel } from "../format";
import { useDashboardStats } from "../hooks/useDashboardStats";

/** Tono del badge por rol (réplica de los colores de badge del SSR). */
const ROLE_TONES: Record<string, BadgeTone> = {
  admin: "danger",
  supervisor: "warning",
  operario: "info",
  entregas: "success",
  mostrador: "muted",
  caja: "muted",
};

function roleTone(role?: string): BadgeTone {
  return role ? (ROLE_TONES[role] ?? "muted") : "muted";
}

/** Una cajita del "Resumen Rápido": rótulo + valor centrados (réplica del SSR). */
function SummaryBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border border-slate-200 px-3 py-2 text-center">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="mt-1 block text-xl font-bold text-slate-900">{value}</span>
    </div>
  );
}

export function SystemInfo() {
  const { user, activeSede } = useAuth();
  const stats = useDashboardStats();

  const isAdmin = user?.role === "admin";
  // Con stats en error/carga no rompemos el panel: mostramos "—" en su lugar.
  const summaryAvailable = !stats.isPending && !stats.isError && stats.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <i className="fas fa-info-circle text-brand" aria-hidden="true" />
          Información del Sistema
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Usuario
          </p>
          <p className="mt-1 text-slate-900">{user?.name ?? "—"}</p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Rol
          </p>
          <p className="mt-1">
            <Badge tone={roleTone(user?.role)}>{roleLabel(user?.role)}</Badge>
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Sede
          </p>
          <p className="mt-1 text-slate-900">{activeSede?.nombre ?? "—"}</p>
        </div>

        {isAdmin && (
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Resumen Rápido
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <SummaryBox
                label="Pendientes"
                value={summaryAvailable ? stats.data.pending : "—"}
              />
              {/* "En Proceso" se alimenta del campo `completed` (estado de orden
                  "completado"), igual que en el SSR; no hay campo `in_process`. */}
              <SummaryBox
                label="En Proceso"
                value={summaryAvailable ? stats.data.completed : "—"}
              />
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Última Actualización
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {formatUpdatedAt(stats.dataUpdatedAt)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
